import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  prompt: string;
  words: string[];
  hints?: string[];
  onSubmit: (answer: string) => void;
}

export default function ScrambleExercise({ prompt, words, hints, onSubmit }: Props) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setAvailable([...words].sort(() => Math.random() - 0.5));
    setSelected([]);
  }, [words]);

  const selectWord = (word: string, index: number) => {
    setSelected([...selected, word]);
    setAvailable(available.filter((_, i) => i !== index));
  };

  const unselectWord = (word: string, index: number) => {
    setAvailable([...available, word]);
    setSelected(selected.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    onSubmit(selected.join(' '));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-lg text-gray-800 font-medium">{prompt}</p>
        <p className="text-sm text-gray-400 mt-1">{t('learning.scrambleHint')}</p>
      </div>

      {hints && hints.length > 0 && (
        <div className="text-center text-sm text-amber-600">
          💡 {hints[0]}
        </div>
      )}

      <div className="min-h-[60px] p-3 bg-indigo-50 rounded-xl border-2 border-dashed border-indigo-200 flex flex-wrap gap-2 items-start">
        {selected.length === 0 && (
          <span className="text-indigo-300 text-sm">{t('learning.placeholderDrop')}</span>
        )}
        {selected.map((word, i) => (
          <button
            key={`sel-${i}`}
            onClick={() => unselectWord(word, i)}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg font-medium text-sm hover:bg-indigo-600 active:scale-95 transition"
          >
            {word}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {available.map((word, i) => (
          <button
            key={`avail-${i}`}
            onClick={() => selectWord(word, i)}
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-medium text-sm text-gray-700 hover:border-indigo-400 active:scale-95 transition"
          >
            {word}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={selected.length === 0}
        className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl text-lg hover:bg-indigo-700 active:scale-98 transition disabled:opacity-40"
      >
        {t('learning.check')}
      </button>
    </div>
  );
}
